<script lang="ts" setup>
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/vue/16/solid';
import type { ChatToolCall } from 'types';
import { computed, ref } from 'vue';

const props = defineProps<{
    toolCall: ChatToolCall;
}>();

const expanded = ref(false);

const isRunning = computed(() => props.toolCall.invocations.some(i => i.status === 'running'));
</script>

<template>
    <div class="my-1">
        <button
            type="button"
            class="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] text-neutral-500 transition-colors hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
            @click="expanded = !expanded">
            <component
                :is="expanded ? ChevronDownIcon : ChevronRightIcon"
                class="size-3 shrink-0" />
            <span class="font-mono">{{ toolCall.name }}</span>
            <span
                v-if="toolCall.invocations.length > 1"
                class="ml-1 rounded-full bg-neutral-200 px-1.5 text-[10px] font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300">
                ×{{ toolCall.invocations.length }}
            </span>
            <span
                v-if="isRunning"
                class="ml-1 size-1.5 animate-pulse rounded-full bg-primary" />
        </button>

        <div
            v-if="expanded"
            class="mt-1 ml-5 space-y-2 rounded-md bg-neutral-50 p-2 text-[11px] dark:bg-neutral-800/50">
            <div
                v-for="(invocation, index) in toolCall.invocations"
                :key="invocation.id"
                :class="index > 0 ? 'border-t border-neutral-200 pt-2 dark:border-neutral-700' : ''">
                <div
                    v-if="toolCall.invocations.length > 1"
                    class="mb-1 font-medium text-neutral-400 dark:text-neutral-500">
                    Call {{ index + 1 }}
                </div>

                <div class="mb-1 font-medium text-neutral-400 dark:text-neutral-500">
                    Input
                </div>
                <pre class="overflow-x-auto whitespace-pre-wrap text-neutral-600 dark:text-neutral-400">{{ JSON.stringify(invocation.input, null, 2) }}</pre>

                <template v-if="invocation.output">
                    <div class="mt-2 mb-1 font-medium text-neutral-400 dark:text-neutral-500">
                        Output
                    </div>
                    <pre class="max-h-32 overflow-x-auto overflow-y-auto whitespace-pre-wrap text-neutral-600 dark:text-neutral-400">{{ invocation.output }}</pre>
                </template>
            </div>
        </div>
    </div>
</template>
